import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crosshair } from "@/components/Crosshair";
import { ImageCropper } from "@/components/ImageCropper";
import { PasswordField } from "@/components/PasswordField";
import { PasswordInput } from "@/components/PasswordInput";
import { FoundingSeatChip } from "@/components/FoundingSeatChip";
import FoundingClaimPrompt from "@/components/founding/FoundingClaimPrompt";
import { toast } from "@/lib/toast";
import {
  Section, UsernameEditor, AboutYouEditor, IdentityPanel, BadgesPanel,
  SpecialtyPicker, BirthdayPanel, SocialHandlesEditor,
} from "@/pages/Settings";
import {
  User, Award, ShieldCheck, AtSign, Pencil, Target, Share2, MapPin, Phone, Cake,
  Camera, Crop, X, Lock, Mail, KeyRound, Save, CheckCircle2, AlertTriangle,
  Smartphone, Monitor, FileCheck, ChevronRight,
} from "lucide-react";

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm transition-colors";

const TABS = [
  { id: "profile", label: "Profile", icon: User },
  { id: "badges", label: "Badges", icon: Award },
  { id: "security", label: "Security", icon: ShieldCheck },
];

// ---------------------------------------------------------------- Avatar / Logo
export function AvatarEditor() {
  const { user, refresh } = useAuth();
  const isBiz = user?.account_type === "business" || user?.is_dealer;
  const [avatar, setAvatar] = useState(user?.avatar_thumb || user?.avatar || null);
  const [uploading, setUploading] = useState(false);
  const [cropper, setCropper] = useState(null);
  const [cropBusy, setCropBusy] = useState(false);

  useEffect(() => { setAvatar(user?.avatar_thumb || user?.avatar || null); }, [user?.avatar, user?.avatar_thumb]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/me/image/decode", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCropper({ source: { dataUrl: data.data_url, width: data.width, height: data.height }, initialTransform: null });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't read that image.");
    } finally { setUploading(false); }
  };

  const openRecrop = async () => {
    setUploading(true);
    try {
      const { data } = await api.get("/me/avatar/source");
      if (!data.original_url) { toast.error("No saved original to re-crop — upload a new one."); return; }
      const dims = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = rej; im.src = data.original_url; });
      setCropper({ source: { dataUrl: data.original_url, width: dims.w, height: dims.h }, initialTransform: data.crop || null });
    } catch { toast.error("Couldn't load the original to re-crop."); }
    finally { setUploading(false); }
  };

  const saveCrop = async (blob, transform) => {
    setCropBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.webp", { type: "image/webp" }));
      const srcBlob = await (await fetch(cropper.source.dataUrl)).blob();
      fd.append("original", new File([srcBlob], "original.webp", { type: srcBlob.type || "image/webp" }));
      fd.append("crop", JSON.stringify(transform));
      const { data: r } = await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(r.avatar_thumb || r.avatar);
      setCropper(null);
      refresh();
      toast.success(isBiz ? "Logo updated." : "Profile photo updated.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't save that image.");
    } finally { setCropBusy(false); }
  };

  const onRemove = async () => {
    setUploading(true);
    try {
      const { data: r } = await api.delete("/me/avatar");
      setAvatar(r.avatar_thumb || r.avatar || null);
      refresh();
      toast.success(isBiz ? "Logo removed." : "Profile photo removed.");
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't remove that photo."); }
    finally { setUploading(false); }
  };

  const noun = isBiz ? "logo" : "photo";
  return (
    <div data-testid="account-avatar-editor" className="flex items-center gap-5">
      <div className="w-24 h-24 border border-sniper bg-obsidian flex items-center justify-center overflow-hidden shrink-0">
        {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" data-testid="account-avatar-img" />
          : <span className="font-heading text-3xl text-muted-foreground" data-testid="account-avatar-monogram">{(user?.username || "?")[0]?.toUpperCase()}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label data-testid="account-avatar-replace"
          className={`inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-2 transition-colors cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          <Camera className="w-3.5 h-3.5" /> {avatar ? `Replace ${noun}` : `Add ${noun}`}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={onFile} disabled={uploading} data-testid="account-avatar-input" />
        </label>
        {avatar && (
          <button onClick={openRecrop} disabled={uploading} data-testid="account-avatar-recrop"
            className="inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-2 transition-colors disabled:opacity-50">
            <Crop className="w-3.5 h-3.5" /> Re-crop
          </button>
        )}
        {avatar && (
          <button onClick={onRemove} disabled={uploading} data-testid="account-avatar-remove"
            className="inline-flex items-center gap-1.5 border border-sniper hover:border-blaze text-secondary-foreground hover:text-blaze font-mono uppercase text-[11px] px-3 py-2 transition-colors disabled:opacity-50">
            <X className="w-3.5 h-3.5" /> Remove
          </button>
        )}
      </div>
      {cropper && (
        <ImageCropper source={cropper.source} initialTransform={cropper.initialTransform}
          label={isBiz ? "Position your logo" : "Position your photo"} busy={cropBusy}
          onSave={saveCrop} onCancel={() => setCropper(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Location (phone + ZIP)
function LocationPanel() {
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/me/contact-changes").then(({ data }) => { setData(data); setPhone(data.phone || ""); }).catch(() => {});
    api.get("/auth/me").then(({ data }) => setZip(data.zip_code || "")).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="font-mono text-xs text-muted-foreground uppercase">Loading…</p>;

  const savePhone = async () => {
    setBusy("phone");
    try { await api.put("/me/contact", { phone: phone.trim() }); toast.success("Phone number saved."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const saveZip = async () => {
    setBusy("zip");
    try {
      const { data: r } = await api.put("/me/contact", { zip_code: zip.trim() });
      toast[r.status === "noop" ? "message" : "success"](r.status === "noop" ? "No change." : "Location updated."); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="account-location-panel" className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Phone className="w-4 h-4 text-olive-bright" />
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Phone number</label>
          <span className="font-mono text-[10px] uppercase text-muted-foreground" data-testid="account-phone-unverified">not verified</span>
        </div>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="account-phone-input" className={inputCls} placeholder="(555) 555-0123" inputMode="tel" />
        <button onClick={savePhone} disabled={busy === "phone" || phone.trim() === (data.phone || "")} data-testid="account-phone-save"
          className="mt-2.5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <Save className="w-3.5 h-3.5" /> {busy === "phone" ? "Saving…" : "Save phone"}
        </button>
        <p className="font-body text-[12px] text-secondary-foreground mt-1.5">Optional. Phone verification isn't available yet, so this is never shown as verified.</p>
      </div>
      <Crosshair className="my-1 opacity-40" />
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <MapPin className="w-4 h-4 text-olive-bright" />
          <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">ZIP code</label>
        </div>
        <input value={zip} onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} data-testid="account-zip-input" className={`${inputCls} max-w-[160px]`} placeholder="90210" inputMode="numeric" />
        <button onClick={saveZip} disabled={busy === "zip" || zip.length !== 5} data-testid="account-zip-save"
          className="mt-2.5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <Save className="w-3.5 h-3.5" /> {busy === "zip" ? "Saving…" : "Save ZIP"}
        </button>
        <p className="font-body text-[12px] text-secondary-foreground mt-1.5">Your ZIP stays private — only the derived city &amp; state show on your public profile.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Email (Security)
function EmailPanel() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const [reqEmail, setReqEmail] = useState("");
  const [reqReason, setReqReason] = useState("");
  const load = useCallback(() => {
    api.get("/me/contact-changes").then(({ data }) => { setData(data); setEmail(data.email || ""); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="font-mono text-xs text-muted-foreground uppercase">Loading…</p>;

  const locked = data.contact_locked;
  const pending = (data.requests || []).find((r) => r.field === "email" && (r.status === "pending" || r.status === "approved"));
  const emailUnchanged = email.trim().toLowerCase() === (data.email || "").toLowerCase();

  const saveEmail = async () => {
    setBusy("email");
    try { await api.put("/me/contact", { email: email.trim().toLowerCase() }); toast.success("Verification email sent — confirm it to finish."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const submitRequest = async () => {
    if (reqReason.trim().length < 10) { toast.error("Add a reason of at least 10 characters."); return; }
    setBusy("request");
    try {
      await api.post("/me/contact-change", { field: "email", new_value: reqEmail.trim().toLowerCase(), reason: reqReason.trim() });
      toast.success("Request submitted. We emailed both your current and new addresses.");
      setReqOpen(false); setReqEmail(""); setReqReason(""); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const cancelRequest = async () => {
    if (!pending) return;
    setBusy("cancel");
    try { await api.post(`/me/contact-change/${pending.request_id}/cancel`); toast.success("Request cancelled."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="account-email-panel">
      <div className="flex items-center gap-2 mb-1.5">
        <Mail className="w-4 h-4 text-olive-bright" />
        <label className="font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">Email address</label>
        {data.email_verified
          ? <span className="font-mono text-[10px] uppercase text-olive-bright inline-flex items-center gap-1" data-testid="account-email-verified"><CheckCircle2 className="w-3 h-3" /> verified</span>
          : <span className="font-mono text-[10px] uppercase text-amber-400 inline-flex items-center gap-1" data-testid="account-email-unverified"><AlertTriangle className="w-3 h-3" /> unverified</span>}
        {locked && <span className="font-mono text-[10px] uppercase text-muted-foreground inline-flex items-center gap-1" data-testid="account-email-locked"><Lock className="w-3 h-3" /> locked</span>}
      </div>
      {locked ? (
        <div>
          <div className="bg-input border border-sniper px-3 py-2.5 font-mono text-sm text-foreground break-all">{data.email}</div>
          <p className="font-body text-[12px] text-secondary-foreground mt-1.5">
            Your email is locked because your identity is verified. Changing it needs a reason, admin approval, and confirmation from the new address — this protects you from account takeover.
          </p>
          {pending ? (
            <div className="mt-3 border border-amber-400/40 bg-amber-400/5 p-3" data-testid="account-pending-request">
              <p className="font-mono text-[11px] uppercase text-amber-400 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" /> {pending.status === "approved" ? "Approved — confirm from your new inbox" : "Change requested — awaiting admin review"}
              </p>
              <p className="font-mono text-xs text-foreground mt-1 break-all">→ {pending.requested_value}</p>
              <button onClick={cancelRequest} disabled={busy === "cancel"} data-testid="account-cancel-request"
                className="mt-2 inline-flex items-center gap-1 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                <X className="w-3 h-3" /> Cancel request
              </button>
            </div>
          ) : reqOpen ? (
            <div className="mt-3 border border-sniper bg-input/50 p-3" data-testid="account-request-form">
              <label className="font-mono text-[10px] uppercase text-secondary-foreground block mb-1">New email address</label>
              <input value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} data-testid="account-request-new-email" className={inputCls} placeholder="new@email.com" autoCapitalize="none" spellCheck={false} />
              <label className="font-mono text-[10px] uppercase text-secondary-foreground block mb-1 mt-3">Reason (min 10 chars)</label>
              <textarea value={reqReason} onChange={(e) => setReqReason(e.target.value)} rows={2} maxLength={500} data-testid="account-request-reason" className={inputCls} placeholder="e.g. I no longer have access to my old inbox." />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={submitRequest} disabled={busy === "request" || !reqEmail.trim() || reqReason.trim().length < 10} data-testid="account-request-submit"
                  className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
                  <Save className="w-3.5 h-3.5" /> {busy === "request" ? "Submitting…" : "Submit request"}
                </button>
                <button onClick={() => { setReqOpen(false); setReqEmail(""); setReqReason(""); }} className="font-mono uppercase text-[10px] text-muted-foreground hover:text-foreground px-2 py-2">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setReqOpen(true)} data-testid="account-request-change-btn"
              className="mt-3 inline-flex items-center gap-2 border border-olive/60 text-olive-bright hover:bg-olive/10 font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
              <KeyRound className="w-3.5 h-3.5" /> Request email change
            </button>
          )}
        </div>
      ) : (
        <div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input" className={inputCls} autoCapitalize="none" spellCheck={false} placeholder="you@email.com" />
          <button onClick={saveEmail} disabled={busy === "email" || emailUnchanged || !email.trim()} data-testid="account-email-save"
            className="mt-2.5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
            <Save className="w-3.5 h-3.5" /> {busy === "email" ? "Saving…" : "Save email"}
          </button>
          <p className="font-body text-[12px] text-secondary-foreground mt-1.5">We'll send a verification link to your new address. It becomes locked once you verify your identity.</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Password
function SecurityPasswordPanel() {
  const { user, applyAuth } = useAuth();
  const hasPassword = user?.has_password !== false;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [pwValid, setPwValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const match = next.length > 0 && next === confirm;
  const onPwValidity = useCallback((v) => setPwValid(v), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwValid) return;
    if (!match) { setError("New passwords don't match."); return; }
    if (hasPassword && !current) { setError("Enter your current password."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: hasPassword ? current : undefined, new_password: next });
      if (data.token) applyAuth(data.token, user);
      setDone(true); setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password updated. Other sessions signed out.");
    } catch (err) { setError(formatError(err.response?.data?.detail) || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="account-password-panel">
      <p className="text-sm text-secondary-foreground mb-4 font-body">
        {hasPassword ? "Change your password. Every other signed-in device will be logged out."
          : "Your account uses Google sign-in. Set a password so you can also log in with your email."}
      </p>
      {done && (
        <div data-testid="account-password-success" className="mb-4 border border-olive/40 bg-olive/10 text-olive-bright text-sm px-3 py-2.5 font-mono flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> Password updated. Other sessions have been signed out.
        </div>
      )}
      <form onSubmit={submit} className="space-y-4 max-w-md">
        {error && <div data-testid="account-password-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{error}</div>}
        {hasPassword && (
          <div>
            <label htmlFor="cur-pw" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Current Password</label>
            <div className="mt-1">
              <PasswordInput id="cur-pw" testId="account-current" inputTestId="account-current" toggleTestId="account-current-toggle" value={current} onChange={setCurrent} autoComplete="current-password" />
            </div>
          </div>
        )}
        <PasswordField id="new-pw" testId="account-new" label={hasPassword ? "New Password" : "Password"} value={next} onChange={setNext} email={user?.email || ""} name={user?.name || ""} onValidityChange={onPwValidity} />
        <div>
          <label htmlFor="confirm-pw" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Confirm New Password</label>
          <div className="mt-1">
            <PasswordInput id="confirm-pw" testId="account-confirm" inputTestId="account-confirm" toggleTestId="account-confirm-toggle" value={confirm} onChange={(v) => { setConfirm(v); if (!confirmTouched) setConfirmTouched(true); }} autoComplete="new-password" />
          </div>
          {confirmTouched && confirm.length > 0 && !match && <p data-testid="account-confirm-mismatch" className="mt-1.5 text-xs font-mono text-blaze">Passwords don't match.</p>}
          {match && <p data-testid="account-confirm-match" className="mt-1.5 text-xs font-mono text-olive-bright">Passwords match.</p>}
        </div>
        <button data-testid="account-password-submit" disabled={!pwValid || !match || busy || (hasPassword && !current)}
          className="bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-base px-6 py-3 transition-colors">
          {busy ? "Saving..." : hasPassword ? "Change Password" : "Set Password"}
        </button>
      </form>
    </div>
  );
}

// 2FA + active-session management are not built yet — surfaced honestly, never faked as "on".
function ComingSoonRow({ icon: Icon, title, detail, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 border border-sniper bg-input/40 px-3 py-3" data-testid={testid}>
      <div className="flex items-start gap-2.5 min-w-0">
        <Icon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-wide text-foreground">{title}</p>
          <p className="font-body text-[12px] text-secondary-foreground mt-0.5">{detail}</p>
        </div>
      </div>
      <span className="font-mono text-[9px] uppercase tracking-wide px-2 py-1 border border-sniper text-muted-foreground shrink-0">Coming soon</span>
    </div>
  );
}

// ---------------------------------------------------------------- Licenses (Badges tab)
function LicensesCard({ me }) {
  const { businessFeaturesEnabled } = useAuth();
  const isBiz = me?.account_type === "business" || me?.is_dealer || me?.is_dealer_applicant;
  const dealerVerified = !!(me?.dealer_verified || me?.ffl_verified);
  if (!isBiz || !businessFeaturesEnabled) return null;
  return (
    <div className="border border-sniper bg-surface p-5" data-testid="account-licenses-card">
      <div className="flex items-center gap-2">
        <FileCheck className="w-5 h-5 text-olive-bright" />
        <h2 className="font-heading text-xl uppercase tracking-tight">Licenses &amp; Compliance</h2>
      </div>
      <p className="text-xs font-body text-secondary-foreground mt-1">
        Your FFL and other license records. A verified FFL earns your <span className="text-[#1F6BA8] font-semibold">DEALER VERIFIED</span> credential and puts you on the map.
      </p>
      <Crosshair className="my-4" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span data-testid="account-dealer-verified-chip"
          className={`font-mono text-[10px] uppercase tracking-wide px-2 py-1 border ${dealerVerified ? "border-[#1F6BA8] text-[#4a9fd6]" : "border-sniper text-muted-foreground"}`}>
          {dealerVerified ? "Dealer verified" : "Not verified yet"}
        </span>
        <Link to="/dealer/licenses" data-testid="account-manage-licenses"
          className="inline-flex items-center gap-1.5 border border-olive/60 text-olive-bright hover:bg-olive/10 font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">
          <FileCheck className="w-3.5 h-3.5" /> Manage licenses <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("profile");

  const reloadMe = useCallback(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    refresh();
  }, [refresh]);
  useEffect(() => { reloadMe(); }, [reloadMe]);

  // Honor the tab from the URL hash so old deep-links still land right (#security, #badges, #socials→profile).
  useEffect(() => {
    const h = (location.hash || "").replace("#", "");
    if (h === "security" || h === "badges") setTab(h);
    else if (h === "socials" || h === "profile") setTab("profile");
  }, [location.hash]);

  if (!user) return null;
  const data = me || user;
  const _nameParts = (data.name || "").trim().split(/\s+/).filter(Boolean);
  const seedFirst = data.first_name || (!data.first_name && !data.last_name ? (_nameParts[0] || "") : "");
  const seedLast = data.last_name || (!data.first_name && !data.last_name ? _nameParts.slice(1).join(" ") : "");

  const selectTab = (id) => { setTab(id); navigate(`/account#${id}`, { replace: true }); };

  return (
    <div className="min-h-screen text-foreground" data-testid="account-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Account</h1>
        <p className="text-sm text-secondary-foreground mt-1 font-body">Everything about your account — your public profile, badges &amp; verification, and login security.</p>

        {/* tabs */}
        <div className="mt-5 flex items-center gap-1 border-b border-sniper" data-testid="account-tabs">
          {TABS.map((t) => {
            const on = tab === t.id;
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => selectTab(t.id)} data-testid={`account-tab-${t.id}`}
                className={`inline-flex items-center gap-2 font-mono uppercase text-xs tracking-wide px-4 py-2.5 border-b-2 -mb-px transition-colors ${on ? "border-olive-bright text-foreground" : "border-transparent text-secondary-foreground hover:text-foreground"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* PROFILE */}
        {tab === "profile" && (
          <div data-testid="account-tab-panel-profile">
            <Section icon={Camera} title="Profile Photo" testId="account-avatar-section"
              subtitle="Shown on your public profile, listings and messages.">
              <AvatarEditor />
            </Section>
            <Section icon={AtSign} title="Username" testId="account-username-section">
              <UsernameEditor current={data.username} onChanged={reloadMe} />
            </Section>
            <Section icon={Pencil} title="About You" testId="account-about-section"
              subtitle="Your real name (private by default) and a short public bio. Buyers read the bio on your profile.">
              <AboutYouEditor handle={data.username} initialFirst={seedFirst} initialLast={seedLast} initialShowName={data.show_name_public} initialBio={data.bio} onChanged={reloadMe} />
            </Section>
            <div id="socials">
              <Section icon={Share2} title="Social Handles" testId="account-socials-section"
                subtitle="Your public social links — shown on your profile and used to credit your #OpticSwap posts.">
                <SocialHandlesEditor initial={data.socials} onChanged={reloadMe} />
              </Section>
            </div>
            <Section icon={MapPin} title="Contact & Location" testId="account-location-section"
              subtitle="Phone (optional) and ZIP. Your ZIP stays private — only city &amp; state show publicly.">
              <LocationPanel />
            </Section>
            <Section icon={Target} title="Specialties" testId="account-specialty-section">
              <SpecialtyPicker initial={data.specialty_tags} onChanged={reloadMe} />
            </Section>
            <Section icon={Cake} title="Birthday" testId="account-birthday-section"
              subtitle="Optional, for birthday perks. Month and day only — never the year.">
              <BirthdayPanel me={data} onChanged={reloadMe} />
            </Section>
          </div>
        )}

        {/* BADGES */}
        {tab === "badges" && (
          <div data-testid="account-tab-panel-badges">
            <Section icon={Award} title="Founding Seat" testId="account-founding-section"
              subtitle="Your permanent founding number, if you claimed a seat.">
              {data.founding_number
                ? <FoundingSeatChip seat={data.founding_number} accountType={data.account_type} testid="account-founding" />
                : <FoundingClaimPrompt mode="inline" />}
            </Section>
            <Section icon={ShieldCheck} title="Verified ID" testId="account-identity-section"
              subtitle="Government-ID verification via Stripe Identity.">
              <IdentityPanel />
            </Section>
            <Section icon={Award} title="Badges" testId="account-badges-section"
              subtitle="Earned automatically from real activity — never self-declared.">
              <BadgesPanel />
            </Section>
            <div className="mt-5"><LicensesCard me={data} /></div>
          </div>
        )}

        {/* SECURITY */}
        {tab === "security" && (
          <div data-testid="account-tab-panel-security">
            <Section icon={Lock} title="Password" testId="account-password-section">
              <SecurityPasswordPanel />
            </Section>
            <Section icon={Mail} title="Email" testId="account-email-section"
              subtitle="A verified identity locks your email against account takeover.">
              <EmailPanel />
            </Section>
            <Section icon={Smartphone} title="Two-Factor Authentication" testId="account-2fa-section"
              subtitle="An extra step at login for stronger account security.">
              <ComingSoonRow icon={Smartphone} title="Authenticator / SMS 2FA" detail="Not available yet — we'll let you know when you can turn it on." testid="account-2fa-row" />
            </Section>
            <Section icon={Monitor} title="Active Sessions" testId="account-sessions-section"
              subtitle="Devices currently signed in to your account.">
              <ComingSoonRow icon={Monitor} title="Signed-in devices" detail="A per-device session list with one-tap sign-out is on the way. Changing your password already signs out every other device now." testid="account-sessions-row" />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
